import { Button, Checkbox, InformationBox, Input, SelectV2, Shimmer, Text } from '@pukka/ui-components';
import { PaymentGatewayErrorModal } from '@pukka/ui-composite';
import { useFlagWithFallback } from '@pukka/unleash';
import { twMerge } from 'tailwind-merge';
import { useSnapshot } from 'valtio';

import { BillingPersistedState } from '../Billing.state';

import { BillingHeader } from './BillingHeader';
import { BillingPriceDetail } from './BillingPriceDetail';
import { BillingPriceSummary } from './BillingPriceSummary';
import { IPaymentState, PaymentState } from './BillingReceipt.state';
import { CycleDiscountModal } from './CycleDiscountModal';
import { TnCModal } from './TnCModal';

interface BillingReceiptProps {
  readonly className?: string;
}

function BillingReceipt({ className }: BillingReceiptProps) {
  const [enableRecurringPayment] = useFlagWithFallback('recurring-payment-backoffice');
  const {
    type,
    cycle,
    cycles,
    couponCode,
    hasActiveOutlet,
    isLoading,
    isPaying,
    isPayable,
    isPayableViaDirectDebit,
    isEnterprise,
    isApplyingCoupon,
    isPaymentGatewayError,
    isTnCModalShown,
    unusedOutletsMessage,
    recurringDiscountMessage,
    tncAccepted,
    isCycleDiscountSchemeModalShown,
    cycleDiscountScheme,
    amount: { outlets, employeeSlots, coupon, total },
  } = useSnapshot(PaymentState);
  const {
    onToggleOutletSelection,
    onChangeCoupon,
    onChangeBillingCycle,
    onApplyCoupon,
    onRemoveCoupon,
    onClosePaymentGatewayErrorModal,
    onPay,
    onPayWithDirectDebit,
    onToggleTnC,
    onToggleTnCModal,
    onToggleCycleDiscountSchemeModal,
    useBillingData,
  } = PaymentState;

  const { pendingPaymentMessage } = useSnapshot(BillingPersistedState);

  useBillingData();

  return (
    <div className={twMerge('flex-1', className)}>
      <Text as="h4" variant="subtitle-2" weight="semibold" color="black" className="mb-4 p-4">
        Payment
      </Text>
      <div className="grid grid-flow-row gap-2.5 rounded-xl bg-neutral-50 p-6">
        {enableRecurringPayment && (
          <BillingHeader disabled={isLoading || !!isPaying || isApplyingCoupon} disableRecurring={!enableRecurringPayment} />
        )}
        <div className="flex items-center justify-between rounded-xl bg-white px-8 py-2">
          <Text as="h5" variant="subtitle-2" weight="medium" className="text-neutral-700">
            Billing Cycle
          </Text>
          <SelectV2
            key={type}
            disabled={isLoading || !!isPaying}
            value={cycle}
            options={cycles as IPaymentState['cycles']}
            size="s"
            onChange={onChangeBillingCycle}
            className="w-52"
          />
        </div>
        {type === 'recurring' && recurringDiscountMessage ? (
          <InformationBox size="md" variant="success" className="view-transition-[header]" icon>
            {recurringDiscountMessage}{' '}
            <Text color="link" className="cursor-pointer underline" onClick={onToggleCycleDiscountSchemeModal}>
              Click here to learn more
            </Text>
          </InformationBox>
        ) : null}
        <BillingPriceDetail
          isLoading={isLoading}
          disabled={!!isPaying}
          outlets={outlets as IPaymentState['amount']['outlets']}
          employeeSlots={employeeSlots}
          isEnterprise={isEnterprise}
          onToggleOutletSelection={onToggleOutletSelection}
        />
        {isEnterprise && (
          <>
            <div className="rounded-xl bg-white px-8 py-4">
              <Text variant="body-2" className="whitespace-pre text-neutral-600">
                expiration date will be extended to
                {'\n'}
                <Text as="span" weight="semibold">
                  {total.newExpiry}
                </Text>
              </Text>
            </div>
            <InformationBox variant="secondary" icon>
              For more details on your bill, please contact your sales representative or contact customer support at 1500970
            </InformationBox>
          </>
        )}
        {!isEnterprise && (
          <>
            {hasActiveOutlet && (
              <div className="rounded-xl bg-white px-8 py-4 view-transition-[coupon]">
                {isLoading ? (
                  <Shimmer size="xxl" className="mb-6" />
                ) : (
                  <div className="mb-6">
                    <Input.Group>
                      <Input
                        disabled={!!coupon.amount || isLoading || !!isPaying}
                        placeholder="Insert coupon code"
                        className="w-[200px] uppercase"
                        value={couponCode}
                        onChangeValue={onChangeCoupon}
                        onSubmit={onApplyCoupon}
                      />
                      <Button
                        onClick={onApplyCoupon}
                        loading={isApplyingCoupon}
                        disabled={!couponCode.length || !!coupon.amount || isLoading || !!isPaying}
                        className="w-20"
                      >
                        Apply
                      </Button>
                    </Input.Group>
                    {coupon.error ? (
                      <Input.Description className="text-red-500" useIcon>
                        {coupon.error}
                      </Input.Description>
                    ) : null}
                  </div>
                )}
                <BillingPriceSummary
                  isLoading={isLoading || isApplyingCoupon}
                  disabled={!!isPaying}
                  coupon={coupon}
                  total={total}
                  onRemoveCoupon={onRemoveCoupon}
                />
              </div>
            )}
            {type === 'recurring' ? (
              <div className="gap-2.5 view-transition-[pay-button]">
                <div className="rounded-xl bg-neutral-100 p-4">
                  <Checkbox id="tnc" data-testid="tnc-checkbox" checked={tncAccepted} onChange={onToggleTnC}>
                    by clicking the button below, you agree to our{' '}
                    <Text as="span" onClick={onToggleTnCModal} color="blue" className="cursor-pointer underline">
                      terms and condition
                    </Text>
                  </Checkbox>
                </div>
                <div className="flex flex-row gap-4">
                  <Button
                    block
                    variant="secondary"
                    icon="cash"
                    loading={isPaying === 'debit'}
                    disabled={!isPayableViaDirectDebit}
                    onClick={onPayWithDirectDebit}
                  >
                    <Text as="span" variant="caption-1" className="w-full text-center md:text-sm">
                      Pay With Direct Debit
                    </Text>
                  </Button>
                  {/* TODO: enable in the next development */}
                  {/* <Button
                    block
                    icon="card-number"
                    disabled={!isPayableViaCreditCard}
                    loading={isPaying === 'credit'}
                    onClick={onPayWithCreditCard}
                  >
                    <Text as="span" variant="caption-1" className="w-full text-center md:text-sm">
                      Pay With Credit Card
                    </Text>
                  </Button> */}
                </div>
              </div>
            ) : (
              <Button
                disabled={!isPayable || isLoading || isApplyingCoupon}
                loading={!!isPaying || isLoading}
                onClick={onPay}
                className="view-transition-[pay-button]"
              >
                {isPaying ? 'Redirecting to Veritrans' : 'Pay'}
              </Button>
            )}
            {unusedOutletsMessage && (
              <InformationBox variant="warning" icon className="view-transition-[unused-outlets]">
                {unusedOutletsMessage}
              </InformationBox>
            )}
            {pendingPaymentMessage ? (
              <InformationBox icon variant="warning" className="view-transition-[pending-payment]">
                {pendingPaymentMessage}
              </InformationBox>
            ) : null}
          </>
        )}
      </div>

      <PaymentGatewayErrorModal
        open={isPaymentGatewayError}
        totalPayment={total.finalPrice - coupon.amount}
        onClose={onClosePaymentGatewayErrorModal}
      />
      <CycleDiscountModal
        open={isCycleDiscountSchemeModalShown}
        data={cycleDiscountScheme}
        onToggleModal={onToggleCycleDiscountSchemeModal}
      />
      <TnCModal open={isTnCModalShown} onToggleModal={onToggleTnCModal} />
    </div>
  );
}

export { BillingReceipt };
